import { styled } from '@mui/system'
import { Fab } from '@mui/material'
import { keyframes } from '@emotion/react'

// Animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -15px, 0);
  }
  70% {
    transform: translate3d(0, -7px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
`

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

export const Container = styled('div')``

// Original Components
export const ForgotButton = styled('a')`
  position: fixed;
  bottom: 30px;
  right: 20px;
  font-size: 18px;
  cursor: pointer;
  padding: 10px;
`

export const ModalContent = styled('div')`
  background-color: #fff;
  max-height: 80vh;
  overflow-y: auto;
  width: 30%;
  margin: 0 auto;
  justify-content: center;
  margin-top: 10vh;
  
  @media (max-width: 768px) {
    width: 90%;
    height: 90vh;
    margin: 10vh auto;
  }
`

export const StyledFab = styled(Fab)`
  background-color: #0F617D;
  color: #FFF;
`

// New Message System Components
export const MessagesContainer = styled('div')`
  position: fixed;
  bottom: 100px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  max-width: 360px;
  
  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    max-width: none;
    bottom: 80px;
  }
`

export const MessagesFab = styled('button')`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  
  &:hover {
    transform: translateY(-4px) scale(1.1);
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(-2px) scale(1.05);
  }
  
  @media (max-width: 768px) {
    right: 15px;
    bottom: 15px;
    width: 52px;
    height: 52px;
  }
`

export const NotificationBadge = styled('div')`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-radius: 50%;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid white;
  animation: ${pulse} 2s infinite;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
`

export const MessagesModal = styled('div')`
  position: fixed;
  bottom: 85px;
  right: 20px;
  width: 380px;
  max-height: 70vh;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 24px;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  animation: ${slideInUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
    animation: ${slideInUp} 0.3s ease-out;
  }
`

export const MessagesHeader = styled('div')`
  padding: 24px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
`

export const MessagesTitle = styled('h3')`
  color: white;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #fff 0%, #e5e7eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

export const MessagesSubtitle = styled('div')`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
`

export const MessagesList = styled('div')`
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 3px;
  }
`

export const MessageCard = styled('div')`
  margin: 0 16px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`

export const MessageHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

export const MessageAuthor = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const AuthorAvatar = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    transform: translateX(-100%);
    animation: ${shimmer} 2s infinite;
  }
`

export const AuthorInfo = styled('div')`
  flex: 1;
`

export const AuthorName = styled('div')`
  color: white;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
`

export const AuthorRole = styled('div')`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
`

export const MessageTime = styled('div')`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  white-space: nowrap;
`

export const MessageContent = styled('div')`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  font-size: 14px;
  margin-bottom: 12px;
`

export const MessageType = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch(props.type) {
      case 'announcement':
        return `
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.3);
        `;
      case 'update':
        return `
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.3);
        `;
      case 'urgent':
        return `
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
          animation: ${pulse} 2s infinite;
        `;
      default:
        return `
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%);
          color: #a78bfa;
          border: 1px solid rgba(139, 92, 246, 0.3);
        `;
    }
  }}
`

export const EmptyMessages = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`

export const EmptyIcon = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  border: 2px dashed rgba(255, 255, 255, 0.2);
`

export const EmptyTitle = styled('h4')`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
`

export const EmptyDescription = styled('p')`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
`

export const MessagesFooter = styled('div')`
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
`

export const WriteMessageButton = styled('button')`
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`

export const CloseButton = styled('button')`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`

// Toast Notification for new messages
export const ToastNotification = styled('div')`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 20px;
  border-radius: 16px;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.3);
  max-width: 320px;
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  gap: 12px;
  animation: ${slideInUp} 0.4s ease-out;
  z-index: 2000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
`

export const ToastIcon = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const ToastContent = styled('div')`
  flex: 1;
  min-width: 0;
`

export const ToastTitle = styled('div')`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ToastMessage = styled('div')`
  font-size: 12px;
  opacity: 0.9;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`